import {
  Description as DescriptionIcon,
  EmojiNature as EmojiNatureIcon,
  Feedback as FeedbackIcon,
  Nature as NatureIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Timeline as TimelineIcon,
} from "@mui/icons-material";
import {
  alpha,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Container,
  Grid,
  Typography,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useState, useContext } from "react";
import FeedbackModal from "../components/FeedbackModal";
import { AuthContext } from "../contexts/AuthContext";
import backgroundImage from "../assets/images/teste.jpg";

function Home() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);

  const features = [
    {
      icon: (
        <SpeedIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
      ),
      title: "Processo Simplificado",
      description:
        "Solicite suas licenças ambientais de forma rápida e descomplicada.",
    },
    {
      icon: (
        <SecurityIcon
          sx={{ fontSize: 40, color: theme.palette.primary.main }}
        />
      ),
      title: "Segurança",
      description:
        "Seus dados e documentos protegidos com os mais altos padrões de segurança.",
    },
    {
      icon: (
        <TimelineIcon
          sx={{ fontSize: 40, color: theme.palette.primary.main }}
        />
      ),
      title: "Acompanhamento em Tempo Real",
      description: "Monitore o status de suas solicitações a qualquer momento.",
    },
    {
      icon: (
        <EmojiNatureIcon
          sx={{ fontSize: 40, color: theme.palette.primary.main }}
        />
      ),
      title: "Sustentabilidade",
      description:
        "Contribua para o desenvolvimento sustentável do meio ambiente.",
    },
  ];

  const licenseTypes = [
    {
      title: "Licença Prévia",
      description: "Solicitada na fase de planejamento do empreendimento.",
      icon: (
        <DescriptionIcon
          sx={{ fontSize: 40, color: theme.palette.primary.main }}
        />
      ),
    },
    {
      title: "Licença de Instalação",
      description: "Necessária para início da construção do empreendimento.",
      icon: (
        <NatureIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
      ),
    },
    {
      title: "Licença de Operação",
      description: "Autoriza o início do funcionamento do empreendimento.",
      icon: (
        <EmojiNatureIcon
          sx={{ fontSize: 40, color: theme.palette.primary.main }}
        />
      ),
    },
  ];

  return (
    <Box sx={{ overflowX: "hidden" }}>
      {/* Hero Section */}
      <Box
        sx={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.7)), url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          height: { xs: "auto", md: "500px" },
          minHeight: { xs: "350px" },
          py: { xs: 6, md: 0 },
          display: "flex",
          alignItems: "center",
          color: "white",
        }}
      >
        <Container sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Grid container spacing={{ xs: 2, md: 4 }}>
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: "2rem", sm: "2.5rem", md: "3.75rem" },
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                Licenciamento Ambiental
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" },
                }}
              >
                Simplifique o processo de licenciamento ambiental do seu
                empreendimento
              </Typography>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={() => navigate("/registro")}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  px: { xs: 3, md: 4 },
                  py: 1.5,
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                Começar Agora
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box
        sx={{
          py: { xs: 4, md: 8 },
          backgroundColor: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        <Container sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Typography
            variant="h4"
            align="center"
            sx={{
              mb: { xs: 3, md: 6 },
              fontWeight: 600,
              fontSize: { xs: "1.2rem", sm: "1.5rem", md: "2.125rem" },
            }}
          >
            Por que escolher nossa plataforma?
          </Typography>
          <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box
                  sx={{
                    textAlign: "center",
                    p: 3,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  {feature.icon}
                  <Typography
                    variant="h6"
                    sx={{
                      my: 2,
                      fontWeight: 600,
                      fontSize: { xs: "1rem", md: "1.25rem" },
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography color="text.secondary">
                    {feature.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* License Types Section */}
      <Box sx={{ py: { xs: 4, md: 8 } }}>
        <Container sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Typography
            variant="h4"
            align="center"
            sx={{
              mb: { xs: 3, md: 6 },
              fontWeight: 600,
              fontSize: { xs: "1.2rem", sm: "1.5rem", md: "2.125rem" },
            }}
          >
            Tipos de Licença
          </Typography>
          <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
            {licenseTypes.map((license, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 2,
                    border: `1px solid ${alpha(
                      theme.palette.primary.main,
                      0.1,
                    )}`,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: `0 10px 20px ${alpha(
                        theme.palette.primary.main,
                        0.1,
                      )}`,
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      flexGrow: 1,
                      textAlign: "center",
                      p: { xs: 2.5, sm: 3, md: 4 },
                    }}
                  >
                    {license.icon}
                    <Typography
                      variant="h5"
                      sx={{
                        my: 2,
                        fontWeight: 600,
                        fontSize: { xs: "1.1rem", md: "1.5rem" },
                      }}
                    >
                      {license.title}
                    </Typography>
                    <Typography color="text.secondary">
                      {license.description}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: "center", pb: 3 }}>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => navigate("/nova-solicitacao")}
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        width: { xs: "100%", sm: "auto" },
                      }}
                    >
                      Solicitar
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box
        sx={{
          position: "fixed",
          bottom: { xs: 16, md: 30 },
          right: { xs: 16, md: 30 },
          zIndex: 999,
        }}
      >
        {user && (
        <Button
          variant="contained"
          color="primary"
          startIcon={<FeedbackIcon />}
          onClick={() => setFeedbackModalOpen(true)}
          sx={{
            borderRadius: "50px",
            textTransform: "none",
            padding: { xs: "10px 16px", md: "12px 24px" },
            fontSize: { xs: "0.8rem", md: "0.875rem" },
            boxShadow: 3,
            "&:hover": {
              boxShadow: 6,
            },
          }}
        >
          Crítica ou sugestão?
        </Button>
        )}
      </Box>

      <FeedbackModal
        open={feedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
      />
    </Box>
  );
}

export default Home;
